import React, { useCallback, useEffect, useState } from 'react';
import { TagIcon, PlusIcon, SearchIcon, TrendingUpIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, PackageIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { supabaseAdmin } from '../lib/supabase';
import { Product, ProductPromotion } from '../types';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useAuth } from '../context/AuthContext';
export function OfertasPage() {
  const {
    addActivityLog
  } = useAuth();
  const [promotions, setPromotions] = useState<ProductPromotion[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'todos' | 'oferta' | 'impulso'>('todos');
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Form state
  const [formProduct, setFormProduct] = useState('');
  const [formType, setFormType] = useState<'oferta' | 'impulso'>('oferta');
  const [formDiscount, setFormDiscount] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formCantidad, setFormCantidad] = useState('');
  const [formRegalo, setFormRegalo] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load promotions
      const {
        data: promoData
      } = await supabaseAdmin.from('product_promotions').select('*').order('created_at', {
        ascending: false
      });
      if (promoData) {
        setPromotions(promoData.map((row: any) => ({
          id: row.id,
          productId: row.product_id,
          productName: row.product_name,
          productSku: row.product_sku,
          tipo: row.tipo,
          descuentoPct: row.descuento_pct,
          precioOferta: row.precio_oferta,
          activo: row.activo,
          fechaInicio: new Date(row.fecha_inicio),
          fechaFin: row.fecha_fin ? new Date(row.fecha_fin) : null,
          notas: row.notas,
          cantidad: row.cantidad || 0,
          regalo: row.regalo || ''
        })));
      }
      // Load ALL products using pagination (Supabase limits 1000 rows per request)
      const {
        data: allProductsData
      } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
      const allProducts = allProductsData || [];
      setProducts(allProducts.map((row: any) => ({
        id: row.id,
        nombre: row.nombre,
        sku: row.sku,
        stock: row.stock,
        stockMinimo: row.stock_minimo,
        precio: row.precio,
        proveedor: row.proveedor
      })));
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const loadDataCallback = useCallback(() => {
    loadData();
  }, []);
  useRealtimeRefresh(['product_promotions', 'products'], loadDataCallback);
  const filteredPromotions = promotions.filter(p => filterType === 'todos' || p.tipo === filterType).filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return p.productName.toLowerCase().includes(query) || p.productSku.toLowerCase().includes(query);
  });
  const activePromotions = promotions.filter(p => p.activo);
  const stats = {
    total: activePromotions.length,
    ofertas: activePromotions.filter(p => p.tipo === 'oferta').length,
    impulsos: activePromotions.filter(p => p.tipo === 'impulso').length
  };
  const resetForm = () => {
    setFormProduct('');
    setProductSearch('');
    setFormType('oferta');
    setFormDiscount('');
    setFormPrice('');
    setFormEndDate('');
    setFormNotes('');
    setFormCantidad('');
    setFormRegalo('');
    setError('');
  };
  const handleSubmit = async () => {
    if (!formProduct) {
      setError('Debe seleccionar un producto');
      return;
    }
    const selectedProd = products.find(p => p.id === formProduct);
    if (!selectedProd) return;
    setIsSubmitting(true);
    setError('');
    try {
      const {
        data,
        error: insertError
      } = await supabaseAdmin.from('product_promotions').insert({
        product_id: selectedProd.id,
        product_name: selectedProd.nombre,
        product_sku: selectedProd.sku,
        tipo: formType,
        descuento_pct: parseFloat(formDiscount) || 0,
        precio_oferta: parseFloat(formPrice) || 0,
        cantidad: parseInt(formCantidad) || 0,
        regalo: formRegalo || '',
        fecha_fin: formEndDate || null,
        notas: formNotes
      }).select().single();
      if (insertError) throw insertError;
      if (data) {
        setPromotions(prev => [{
          id: data.id,
          productId: data.product_id,
          productName: data.product_name,
          productSku: data.product_sku,
          tipo: data.tipo,
          descuentoPct: data.descuento_pct,
          precioOferta: data.precio_oferta,
          activo: data.activo,
          fechaInicio: new Date(data.fecha_inicio),
          fechaFin: data.fecha_fin ? new Date(data.fecha_fin) : null,
          notas: data.notas,
          cantidad: data.cantidad || 0,
          regalo: data.regalo || ''
        }, ...prev]);
      }
      const selectedProd = products.find(p => p.id === formProduct);
      addActivityLog('Creó promoción', `Producto: ${selectedProd?.nombre || formProduct} | Tipo: ${formType}`);
      setIsModalOpen(false);
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la promoción');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Está seguro de eliminar esta promoción?')) return;
    try {
      const promo = promotions.find(p => p.id === id);
      const {
        error
      } = await supabaseAdmin.from('product_promotions').delete().eq('id', id);
      if (!error) {
        setPromotions(prev => prev.filter(p => p.id !== id));
        addActivityLog('Eliminó promoción', `Producto: ${promo?.productName || id} | Tipo: ${promo?.tipo || ''}`);
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };
  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const {
        error
      } = await supabaseAdmin.from('product_promotions').update({
        activo: !currentStatus
      }).eq('id', id);
      if (!error) {
        setPromotions(prev => prev.map(p => p.id === id ? {
          ...p,
          activo: !currentStatus
        } : p));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };
  return <div className="space-y-6" data-id="element-3629" data-mp="1s4sk43pazrej">
      <PageHeader title="Ofertas e Impulsos" subtitle="Gestión de productos destacados para vendedores" icon={<TagIcon className="w-6 h-6" data-id="element-3631" data-mp="1n6r09gk1zo20" />} data-id="element-3630" data-mp="1s40r90n3c7ry">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto" data-id="element-3632" data-mp="3b25g5a5csvsa">
          <div className="relative w-full sm:w-64" data-id="element-3633" data-mp="3g1hypip6zgp2">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3634" data-mp="3g0ysv0ctgire" />
            <input type="text" placeholder="Buscar producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" data-id="element-3635" data-mp="321pd21sivqny" />
          </div>
          <Button onClick={() => {
          resetForm();
          setIsModalOpen(true);
        }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3637" data-mp="2naqap8ia5yct" />} data-id="element-3636" data-mp="2jd2apr1qdfe2">
            Nueva Promoción
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-stagger-1" data-id="element-3638" data-mp="2eco144khdxdx">
        <StatsCard title="Total Activas" value={stats.total} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-3640" data-mp="1prjbo32xugzm" />} variant="primary" data-id="element-3639" data-mp="1uqcmfnqubtw3" />
        <StatsCard title="En Oferta" value={stats.ofertas} icon={<TagIcon className="w-5 h-5" data-id="element-3642" data-mp="24mpg4lmdy7qu" />} variant="success" data-id="element-3641" data-mp="1ptw2l5qoc1ot" />
        <StatsCard title="Impulsos" value={stats.impulsos} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3644" data-mp="2dofa9z7njksb" />} variant="warning" data-id="element-3643" data-mp="1zo4fil381m1v" />
      </div>

      <Card padding="none" className="animate-stagger-2" data-id="element-3645" data-mp="28pcfr8paswyl">
        <div className="flex border-b border-slate-200" data-id="element-3646" data-mp="2nfa3xon89mns">
          <button onClick={() => setFilterType('todos')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'todos' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3647" data-mp="2neqpwj0p7if1">
            Todos
          </button>
          <button onClick={() => setFilterType('oferta')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'oferta' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3648" data-mp="2scjr0xn59m8g">
            Ofertas
          </button>
          <button onClick={() => setFilterType('impulso')} className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${filterType === 'impulso' ? 'border-amber-600 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3649" data-mp="8w6rrhs74viw">
            Impulsos
          </button>
        </div>

        {isLoading ? <div className="p-12 text-center" data-id="element-3650" data-mp="wn3woalkqskn">
            <div className="w-8 h-8 border-3 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" data-id="element-3651" data-mp="sime2eqyalbg" />
            <p className="text-slate-500" data-id="element-3652" data-mp="3hd1jc13jmtho">Cargando promociones...</p>
          </div> : filteredPromotions.length === 0 ? <div className="p-8 sm:p-16 text-center" data-id="element-3653" data-mp="1sruh6golj5bi">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3654" data-mp="1st5hg6iu1uxv">
              <TagIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-3655" data-mp="1wrd2hdkbxiev" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-3656" data-mp="21r7slewa63yv">
              No hay promociones
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-3657" data-mp="2bilxk2703vze">
              Agrega productos en oferta o impulso para que los vendedores los
              ofrezcan primero.
            </p>
          </div> : <>
            {/* ── MOBILE CARD VIEW (< sm) ── */}
            <div className="sm:hidden divide-y divide-slate-100" data-id="element-3658" data-mp="2kgxjhqwxa5li">
              {filteredPromotions.map(promo => <div key={promo.id} className={`p-3.5 ${!promo.activo ? 'opacity-60' : ''}`} data-id="element-3659" data-mp="q50qvumqutov">
                  <div className="flex items-start justify-between gap-2" data-id="element-3660" data-mp="v4vhdx2lxh9e">
                    <div className="min-w-0 flex-1" data-id="element-3661" data-mp="1ye7xq5i71y6r">
                      <p className="font-semibold text-slate-900 text-sm leading-tight" data-id="element-3662" data-mp="1yh3y00hikyyd">
                        {promo.productName}
                      </p>
                      <p className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-3663" data-mp="27gqwadey5on7">
                        {promo.productSku}
                      </p>
                      {promo.notas && <p className="text-[11px] text-slate-400 mt-0.5 italic truncate" data-id="element-3664" data-mp="2hd2c3ddqwwme">
                          "{promo.notas}"
                        </p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3665" data-mp="2q54br9fzq8vm">
                      {promo.tipo === 'oferta' ? <Badge variant="success" size="sm" data-id="element-3666" data-mp="2m5dmja6440t7">
                          Oferta
                        </Badge> : <Badge variant="warning" size="sm" data-id="element-3667" data-mp="2v61r7e9m8otf">
                          Impulso
                        </Badge>}
                      <button onClick={() => handleToggleActive(promo.id, promo.activo)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${promo.activo ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-3668" data-mp="3eozax7wx9ldu">
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${promo.activo ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-3669" data-mp="2w0asqtptpu5r" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs" data-id="element-3670" data-mp="2r0ohgptk2nzl">
                    {promo.descuentoPct > 0 && <span className="font-bold text-emerald-600" data-id="element-3671" data-mp="2d2zxrlb85mw4">
                        {promo.descuentoPct}% desc.
                      </span>}
                    {promo.precioOferta > 0 && <span className="font-bold text-blue-600" data-id="element-3672" data-mp="2a26gt8rs4ieq">
                        RD${promo.precioOferta.toLocaleString()}
                      </span>}
                    {promo.cantidad > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-bold" data-id="element-3673" data-mp="1vb7f8hpd2u4n">
                        {promo.cantidad} uds.
                      </span>}
                    {promo.regalo && <span className="text-slate-600" data-id="element-3674" data-mp="1m9hjx0sczb1l">🎁 {promo.regalo}</span>}
                    <button onClick={() => handleDelete(promo.id)} className="ml-auto p-1 text-slate-400 hover:text-rose-600 rounded-lg transition-colors" data-id="element-3675" data-mp="1hchdupvie8ah">
                      <TrashIcon className="w-3.5 h-3.5" data-id="element-3676" data-mp="3boxk3qob1mfc" />
                    </button>
                  </div>
                </div>)}
            </div>

            {/* ── DESKTOP TABLE (sm+) ── */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-3677" data-mp="3f5afy7m4of7b">
              <table className="w-full text-sm" data-id="element-3678" data-mp="3j7x0rokdszv0">
                <thead className="bg-slate-50 border-b border-slate-200" data-id="element-3679" data-mp="3j6lwzoqxrs3w">
                  <tr data-id="element-3680" data-mp="3o0gohecs9bdj">
                    <th className="px-5 py-3 text-left font-semibold text-slate-600" data-id="element-3681" data-mp="3o219odd7jhyq">
                      Producto
                    </th>
                    <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-3682" data-mp="2w9kc0eumx8qs">
                      Tipo
                    </th>
                    <th className="px-5 py-3 text-right font-semibold text-slate-600" data-id="element-3683" data-mp="30c6vnsh5jncw">
                      Beneficio
                    </th>
                    <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-3684" data-mp="3a8ti2lp4mmh1">
                      Cantidad
                    </th>
                    <th className="px-5 py-3 text-left font-semibold text-slate-600" data-id="element-3685" data-mp="kiq45g4sv12g">
                      Regalo
                    </th>
                    <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-3686" data-mp="3vz1je93bql9s">
                      Vigencia
                    </th>
                    <th className="px-5 py-3 text-center font-semibold text-slate-600" data-id="element-3687" data-mp="3qzf7c2z8fb2k">
                      Estado
                    </th>
                    <th className="px-5 py-3 w-16" data-id="element-3688" data-mp="4t0haq1c720a"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3689" data-mp="3d8tmi8q7wr21">
                  {filteredPromotions.map(promo => <tr key={promo.id} className={`hover:bg-slate-50 transition-colors ${!promo.activo ? 'opacity-60' : ''}`} data-id="element-3690" data-mp="38a8wf24ojnrb">
                      <td className="px-5 py-4" data-id="element-3691" data-mp="3i86ipk2zbaha">
                        <p className="font-semibold text-slate-900" data-id="element-3692" data-mp="3i6bu9vsi108g">
                          {promo.productName}
                        </p>
                        <p className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-3693" data-mp="2pjhehc6mmp1b">
                          {promo.productSku}
                        </p>
                        {promo.notas && <p className="text-xs text-slate-400 mt-1 italic" data-id="element-3694" data-mp="19rn9x70ouqnk">
                            "{promo.notas}"
                          </p>}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3695" data-mp="101uat5hqg407">
                        {promo.tipo === 'oferta' ? <Badge variant="success" size="sm" data-id="element-3696" data-mp="1nreuxrpmh7b2">
                            Oferta
                          </Badge> : <Badge variant="warning" size="sm" data-id="element-3697" data-mp="1sof2y8618k4t">
                            Impulso
                          </Badge>}
                      </td>
                      <td className="px-5 py-4 text-right" data-id="element-3698" data-mp="1iqsaeivt8u2d">
                        {promo.descuentoPct > 0 && <span className="block font-bold text-emerald-600" data-id="element-3699" data-mp="3e7hhgfyh1qz">
                            {promo.descuentoPct}% desc.
                          </span>}
                        {promo.precioOferta > 0 && <span className="block font-bold text-blue-600" data-id="element-3700" data-mp="u0vb62t6hjp8">
                            RD${promo.precioOferta.toLocaleString()}
                          </span>}
                        {promo.descuentoPct === 0 && promo.precioOferta === 0 && <span className="text-slate-400" data-id="element-3701" data-mp="1hq7lj608rxt4">—</span>}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3702" data-mp="1dqp6urz0kiyq">
                        {promo.cantidad > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-50 text-blue-700" data-id="element-3703" data-mp="18wugj5owl5qo">
                            {promo.cantidad}
                          </span> : <span className="text-slate-400" data-id="element-3704" data-mp="1wohyuz83n8hc">—</span>}
                      </td>
                      <td className="px-5 py-4 text-left text-sm text-slate-700" data-id="element-3705" data-mp="2417s314rjiij">
                        {promo.regalo || <span className="text-slate-400" data-id="element-3706" data-mp="2913nwd3b43n0">—</span>}
                      </td>
                      <td className="px-5 py-4 text-center text-slate-600" data-id="element-3707" data-mp="2hzpfexqraszx">
                        <div className="text-xs" data-id="element-3708" data-mp="2hxkz1gbo81ir">
                          Desde: {promo.fechaInicio.toLocaleDateString('es-DO')}
                        </div>
                        {promo.fechaFin && <div className="text-xs mt-0.5" data-id="element-3709" data-mp="2rvina3tj5ybd">
                            Hasta: {promo.fechaFin.toLocaleDateString('es-DO')}
                          </div>}
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3710" data-mp="35pjr3bzcabil">
                        <button onClick={() => handleToggleActive(promo.id, promo.activo)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${promo.activo ? 'bg-indigo-600' : 'bg-slate-300'}`} data-id="element-3711" data-mp="35r35ex1fqchq">
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${promo.activo ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-3712" data-mp="of7r6f7o8z6d" />
                        </button>
                      </td>
                      <td className="px-5 py-4 text-center" data-id="element-3713" data-mp="13d07vaoyftm5">
                        <button onClick={() => handleDelete(promo.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Eliminar" data-id="element-3714" data-mp="13b6wji3ecl7t">
                          <TrashIcon className="w-4 h-4" data-id="element-3715" data-mp="1h2turngwg3t8" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </Card>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Nueva Promoción" size="md" data-id="element-3716" data-mp="1r7trdu8am39h">
        <ModalBody data-id="element-3717" data-mp="1mbuz3vjug13s">
          <div className="space-y-5" data-id="element-3718" data-mp="1i8gf6tssb0l2">
            {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700 flex items-center gap-2" data-id="element-3719" data-mp="1i9zvgkefxbmu">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-3720" data-mp="29zcqhpz74x68" />
                {error}
              </div>}

            {/* Product Search */}
            <div className="relative" data-id="element-3721" data-mp="257342jxaif8p">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3722" data-mp="217172o2i29zr">
                Producto *
              </label>
              <div className="relative" data-id="element-3723" data-mp="2xpz03loumpic">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3724" data-mp="2xr1rrvtzwvzb" />
                <input type="text" value={formProduct ? products.find(p => p.id === formProduct)?.nombre : productSearch} onChange={e => {
                setProductSearch(e.target.value);
                setFormProduct('');
                setShowProductDropdown(true);
              }} onFocus={() => setShowProductDropdown(true)} placeholder="Buscar producto..." className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-3725" data-mp="2swe05o3p3lgi" />
                {formProduct && <button onClick={() => {
                setFormProduct('');
                setProductSearch('');
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3726" data-mp="3bvo6whuzyrg">
                    ×
                  </button>}
              </div>

              {showProductDropdown && !formProduct && <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto" data-id="element-3727" data-mp="3rwz2fuazzi9y">
                  {products.filter(p => !productSearch || p.nombre.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 20).map(p => <button key={p.id} onClick={() => {
                setFormProduct(p.id);
                setShowProductDropdown(false);
              }} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center gap-3" data-id="element-3728" data-mp="2givasurvu682">
                        <PackageIcon className="w-4 h-4 text-slate-400" data-id="element-3729" data-mp="2mzdmtnc82q71" />
                        <div data-id="element-3730" data-mp="2rxoupsiyiqmm">
                          <p className="text-sm font-medium text-slate-900" data-id="element-3731" data-mp="2rvu6o5ce2ieb">
                            {p.nombre}
                          </p>
                          <p className="text-xs text-slate-500 font-mono" data-id="element-3732" data-mp="1z8hoe7d9gwqt">
                            {p.sku}
                          </p>
                        </div>
                      </button>)}
                </div>}
              {showProductDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowProductDropdown(false)} data-id="element-3733" data-mp="1bix0rb65xbb7" />}
            </div>

            {/* Type Selection */}
            <div data-id="element-3734" data-mp="3tsrvhtdw5v5d">
              <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-3735" data-mp="3tr8g05027o4n">
                Tipo de Promoción *
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-3736" data-mp="3pok2vl4bbv1v">
                <button type="button" onClick={() => setFormType('oferta')} className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${formType === 'oferta' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-emerald-200'}`} data-id="element-3737" data-mp="3kqio3cdx6hw7">
                  Oferta
                </button>
                <button type="button" onClick={() => setFormType('impulso')} className={`py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${formType === 'impulso' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'border-slate-200 text-slate-500 hover:border-amber-200'}`} data-id="element-3738" data-mp="321vk6x7hbyk7">
                  Impulso
                </button>
              </div>
            </div>

            {/* Pricing/Discount */}
            <div className="grid grid-cols-2 gap-4" data-id="element-3739" data-mp="2ifk5igdu9v2d">
              <Input label="Descuento (%)" type="number" placeholder="Ej: 10" value={formDiscount} onChange={e => setFormDiscount(e.target.value)} data-id="element-3740" data-mp="2icxqjvomcda4" />
              <Input label="Precio Especial (RD$)" type="number" placeholder="Ej: 1500" value={formPrice} onChange={e => setFormPrice(e.target.value)} data-id="element-3741" data-mp="2w7qbxar6wnom" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-3742" data-mp="2rfysdk6gbi7z">
              <Input label="Cantidad" type="number" placeholder="Ej: 12" value={formCantidad} onChange={e => setFormCantidad(e.target.value)} data-id="element-3743" data-mp="2refd9wwj7d7s" />
              <Input label="Regalo" placeholder="Ej: 1 unidad gratis" value={formRegalo} onChange={e => setFormRegalo(e.target.value)} data-id="element-3744" data-mp="18haoctt7l3qz" />
            </div>

            <Input label="Fecha de Fin (Opcional)" type="date" value={formEndDate} min={new Date().toISOString().split('T')[0]} onChange={e => setFormEndDate(e.target.value)} data-id="element-3745" data-mp="1i62csrz295x5" />

            <div data-id="element-3746" data-mp="kjd4y1hofz3d">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-3747" data-mp="kjv8lj6o5flb">
                Notas para el vendedor
              </label>
              <textarea className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none h-20 resize-none" placeholder="Ej: Ofrecer por compra mayor a 5 unidades..." value={formNotes} onChange={e => setFormNotes(e.target.value)} data-id="element-3748" data-mp="zi72ji2nqa8s" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3749" data-mp="37y5x56e5v2a">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-3750" data-mp="88uis88q0rju">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!formProduct} data-id="element-3751" data-mp="d7p2dceiik50">
            Guardar Promoción
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}